import {
  fiveBandFromDistribution,
  formatFiveBandLabel,
} from '@digital-twins/deck-ui/components/charts/diverging'
import { AnalystChartDatum, AnalystChartSpec } from '@digital-twins/shared-types'

import { typedScalePoints } from './featuredPlotRouting'

interface ScaleBandPoint {
  rawValue: unknown
  value: number
}

export interface ScaleDistributionRow {
  id: string
  label: string
  bands: NonNullable<ReturnType<typeof fiveBandFromDistribution>>
  captions: Array<string>
}

function visibleValue(datum: AnalystChartDatum): number | null {
  if (datum.isSuppressed || datum.value === null) {
    return null
  }
  return datum.value
}

/**
 * A typed rating distribution → five-band rows (one per series). The scale's
 * ends are the stamped declared scale's (`typedScalePoints`), never the
 * observed answers: a 1-5 scale nobody answered 1 on still bands 1-5.
 */
export function mapScaleDistribution(
  chart: AnalystChartSpec,
): Array<ScaleDistributionRow> | null {
  const typed = typedScalePoints(chart)
  if (!typed) {
    return null
  }
  const { min: scaleMin, max: scaleMax } = typed
  const rows: Array<ScaleDistributionRow> = []
  for (const series of chart.series) {
    const points: Array<ScaleBandPoint> = []
    series.data.forEach((datum, index) => {
      const value = visibleValue(datum)
      if (value === null || value <= 0) {
        return
      }
      points.push({ rawValue: typed.points[index], value })
    })
    if (points.length === 0) {
      continue
    }
    const bands = fiveBandFromDistribution(points, scaleMin, scaleMax, 'higher_is_better')
    if (!bands) {
      continue
    }
    const label = series.label || series.id
    rows.push({
      id: series.id,
      label,
      bands,
      captions: bands.map((band) => formatFiveBandLabel(band.count, band.total)),
    })
  }
  return rows.length > 0 ? rows : null
}
